class CosmeaBeforeAfter extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-before-after-range]');
    if (!this.input) return;

    this.updatePosition = this.updatePosition.bind(this);
    this.input.addEventListener('input', this.updatePosition);
    this.updatePosition();
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener('input', this.updatePosition);
    }
  }

  updatePosition() {
    this.style.setProperty('--cosmea-before-position', `${this.input.value}%`);
  }
}

if (!customElements.get('cosmea-before-after')) {
  customElements.define('cosmea-before-after', CosmeaBeforeAfter);
}

function initCosmeaVisualStories() {
  document.querySelectorAll('.cosmea-visual-story--sticky').forEach((section) => {
    const mediaItems = Array.from(section.querySelectorAll('[data-cosmea-story-media]'));
    const panels = Array.from(section.querySelectorAll('[data-cosmea-story-panel]'));
    if (!mediaItems.length || !panels.length) return;

    let activeIndex = null;
    const activate = (index) => {
      const nextIndex = String(index);
      if (activeIndex === nextIndex) return;
      activeIndex = nextIndex;

      mediaItems.forEach((item) => {
        item.classList.toggle('is-active', item.dataset.cosmeaStoryMedia === nextIndex);
      });
      panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.cosmeaStoryPanel === nextIndex);
      });
    };

    activate(0);

    let ticking = false;

    const updateActivePanel = () => {
      const targetY = window.innerHeight * 0.52;
      let closestPanel = panels[0];
      let closestDistance = Number.POSITIVE_INFINITY;

      panels.forEach((panel) => {
        const rect = panel.getBoundingClientRect();
        const panelCenter = rect.top + rect.height / 2;
        const distance = Math.abs(panelCenter - targetY);

        if (rect.bottom >= 0 && rect.top <= window.innerHeight && distance < closestDistance) {
          closestPanel = panel;
          closestDistance = distance;
        }
      });

      if (closestPanel) {
        activate(closestPanel.dataset.cosmeaStoryPanel || 0);
      }
      ticking = false;
    };

    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateActivePanel);
    };

    updateActivePanel();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
  });
}

function initCosmeaStickyBuy() {
  document.querySelectorAll('.cosmea-sticky-buy').forEach((bar) => {
    const button = bar.querySelector('.cosmea-sticky-buy__button');
    const formId = button && button.getAttribute('form');
    if (!formId) return;

    const productForm = document.getElementById(formId);
    const mainSubmit =
      (productForm && productForm.querySelector('.product-form__submit')) ||
      document.querySelector('.product-form__submit');
    if (!mainSubmit) return;

    const syncButtonState = () => {
      const isLoading = mainSubmit.classList.contains('loading');
      const isDisabled =
        mainSubmit.disabled || mainSubmit.getAttribute('aria-disabled') === 'true';

      button.classList.toggle('is-loading', isLoading);
      button.disabled = isDisabled;
      button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
    };

    const update = () => {
      const rect = mainSubmit.getBoundingClientRect();
      const shouldShow = rect.bottom < 0;
      bar.classList.toggle('is-visible', shouldShow);
      bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      syncButtonState();
    };

    update();
    syncButtonState();

    const observer = new MutationObserver(syncButtonState);
    observer.observe(mainSubmit, {
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled'],
    });

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  });
}

class CosmeaCartQuantity extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedOnce) return;
    this.isConnectedOnce = true;

    this.valueElement = this.querySelector('.cosmea-cart-quantity__value');
    this.controls = this.querySelector('.cosmea-cart-quantity__controls');
    this.submitButton = this.querySelector('[name="add"]');
    this.statusElement = this.querySelector('.cosmea-cart-quantity__status');
    this.cartData = null;
    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);

    this.syncQuantity(Number.parseInt(this.dataset.quantity, 10) || 0);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'cosmea-cart-quantity' && event.cartData?.items) {
          this.applyCart(event.cartData);
          return;
        }
        return this.refreshFromCart();
      });

      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variantId = event?.data?.variant?.id;
        if (!variantId) return;
        this.dataset.variantId = String(variantId);
        return this.refreshFromCart();
      });
    }

    if (Number.parseInt(this.dataset.quantity, 10) > 0) this.refreshFromCart();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.cartUpdateUnsubscriber?.();
    this.variantChangeUnsubscriber?.();
  }

  get variantId() {
    return Number.parseInt(this.dataset.variantId, 10);
  }

  get step() {
    return Number.parseInt(this.dataset.step, 10) || 1;
  }

  get max() {
    const value = Number.parseInt(this.dataset.max, 10);
    return Number.isFinite(value) ? value : null;
  }

  onClick(event) {
    const actionButton = event.target.closest('[data-cosmea-quantity-action]');
    if (!actionButton || !this.contains(actionButton)) return;
    event.preventDefault();
    const direction = actionButton.dataset.cosmeaQuantityAction === 'increase' ? 1 : -1;
    this.changeQuantity(direction * this.step, actionButton);
  }

  syncQuantity(quantity) {
    const safeQuantity = Math.max(0, Number.parseInt(quantity, 10) || 0);
    this.dataset.quantity = String(safeQuantity);
    this.classList.toggle('is-in-cart', safeQuantity > 0);
    if (this.valueElement) this.valueElement.textContent = safeQuantity;
    if (this.controls) this.controls.hidden = safeQuantity === 0;
    if (this.submitButton) this.submitButton.hidden = safeQuantity > 0;

    const productInfo = this.closest('product-info');
    if (productInfo) productInfo.classList.toggle('cosmea-product-in-cart', safeQuantity > 0);

    const increaseButton = this.querySelector('[data-cosmea-quantity-action="increase"]');
    if (increaseButton) increaseButton.disabled = this.max !== null && safeQuantity >= this.max;
  }

  applyCart(cart) {
    if (!cart?.items) return;
    this.cartData = cart;
    const matchingItems = cart.items.filter((item) => Number(item.variant_id) === this.variantId);
    const quantity = matchingItems.reduce((total, item) => total + Number(item.quantity || 0), 0);
    this.syncQuantity(quantity);
  }

  refreshFromCart() {
    if (!window.routes?.cart_url) return Promise.resolve();
    return fetch(`${window.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
        return response.json();
      })
      .then((cart) => this.applyCart(cart))
      .catch((error) => console.error(error));
  }

  changeQuantity(delta, trigger) {
    if (this.hasAttribute('aria-busy')) return;

    const ensureCart = this.cartData ? Promise.resolve(this.cartData) : this.refreshFromCart().then(() => this.cartData);
    this.setBusy(true);

    ensureCart
      .then((cart) => {
        const matchingItems = cart?.items?.filter((item) => Number(item.variant_id) === this.variantId) || [];
        const lineItem = matchingItems[0];
        if (!lineItem) throw new Error('This product is no longer in the cart.');

        const nextLineQuantity = Math.max(0, Number(lineItem.quantity) + delta);
        const body = {
          id: lineItem.key,
          quantity: nextLineQuantity,
          sections: ['cart-icon-bubble'],
          sections_url: window.location.pathname,
        };

        return fetch(window.routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify(body),
        });
      })
      .then((response) => response.json())
      .then((cart) => {
        if (cart.errors) throw new Error(cart.errors);
        this.applyCart(cart);
        this.updateCartIcon(cart.sections?.['cart-icon-bubble']);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          return publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cosmea-cart-quantity',
            cartData: cart,
            variantId: String(this.variantId),
          });
        }
      })
      .then(() => trigger?.focus())
      .catch((error) => {
        if (this.statusElement) this.statusElement.textContent = error.message;
        console.error(error);
        return this.refreshFromCart();
      })
      .finally(() => this.setBusy(false));
  }

  updateCartIcon(sectionHtml) {
    if (!sectionHtml) return;
    const cartIcon = document.getElementById('cart-icon-bubble');
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const source = parsed.querySelector('.shopify-section');
    if (cartIcon && source) cartIcon.innerHTML = source.innerHTML;
  }

  setBusy(isBusy) {
    this.toggleAttribute('aria-busy', isBusy);
    this.classList.toggle('is-loading', isBusy);
    this.querySelectorAll('[data-cosmea-quantity-action]').forEach((button) => {
      button.disabled = isBusy;
    });
    if (!isBusy) this.syncQuantity(this.dataset.quantity);
  }
}

if (!customElements.get('cosmea-cart-quantity')) {
  customElements.define('cosmea-cart-quantity', CosmeaCartQuantity);
}

class CosmeaIngredientSlider extends HTMLElement {
  connectedCallback() {
    if (this.isConnectedOnce) return;
    this.isConnectedOnce = true;
    this.viewport = this.querySelector('.cosmea-key-ingredients__viewport');
    this.track = this.querySelector('.cosmea-key-ingredients__track');
    this.previousButton = this.querySelector('[data-cosmea-ingredient-previous]');
    this.nextButton = this.querySelector('[data-cosmea-ingredient-next]');
    if (!this.viewport || !this.track) return;

    this.scrollContainer = window.matchMedia('(max-width: 749px)').matches ? this.track : this.viewport;
    this.onScroll = () => window.requestAnimationFrame(() => this.updateControls());
    this.previousButton?.addEventListener('click', () => this.scrollCards(-1));
    this.nextButton?.addEventListener('click', () => this.scrollCards(1));
    this.scrollContainer.addEventListener('scroll', this.onScroll, { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.refresh());
    this.resizeObserver.observe(this);
    window.requestAnimationFrame(() => this.refresh());
  }

  disconnectedCallback() {
    this.scrollContainer?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
  }

  refresh() {
    const nextContainer = window.matchMedia('(max-width: 749px)').matches ? this.track : this.viewport;
    if (nextContainer !== this.scrollContainer) {
      this.scrollContainer.removeEventListener('scroll', this.onScroll);
      this.scrollContainer = nextContainer;
      this.scrollContainer.addEventListener('scroll', this.onScroll, { passive: true });
    }
    this.updateControls();
  }

  scrollCards(direction) {
    const card = this.track.querySelector('.cosmea-key-ingredient-card');
    if (!card) return;
    const gap = Number.parseFloat(window.getComputedStyle(this.track).columnGap) || 0;
    this.scrollContainer.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
  }

  updateControls() {
    const maxScroll = this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth;
    const hasOverflow = maxScroll > 2;
    if (this.previousButton) {
      this.previousButton.hidden = !hasOverflow;
      this.previousButton.disabled = this.scrollContainer.scrollLeft <= 2;
    }
    if (this.nextButton) {
      this.nextButton.hidden = !hasOverflow;
      this.nextButton.disabled = this.scrollContainer.scrollLeft >= maxScroll - 2;
    }
  }
}

if (!customElements.get('cosmea-ingredient-slider')) {
  customElements.define('cosmea-ingredient-slider', CosmeaIngredientSlider);
}

function initCosmeaProductNav() {
  const navSections = Array.from(document.querySelectorAll('.cosmea-product-nav-section'));
  if (!navSections.length) return;
  const headerSection = document.querySelector('.section-header');
  const navGroups = navSections.map((section) => ({
    section,
    links: Array.from(section.querySelectorAll('.cosmea-product-nav__link'))
      .map((link) => ({
        link,
        target: link.hash ? document.getElementById(decodeURIComponent(link.hash.slice(1))) : null,
      }))
      .filter((item) => item.target),
  }));

  const setActiveLink = (group) => {
    const activationLine = group.section.getBoundingClientRect().bottom + 16;
    const orderedLinks = [...group.links].sort((first, second) => {
      if (first.target === second.target) return 0;
      return first.target.compareDocumentPosition(second.target) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });
    let activeItem = null;

    orderedLinks.forEach((item) => {
      if (item.target.getBoundingClientRect().top <= activationLine) activeItem = item;
    });

    const reachedPageEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    if (reachedPageEnd && orderedLinks.length) activeItem = orderedLinks[orderedLinks.length - 1];

    group.links.forEach((item) => {
      const isActive = item === activeItem;
      item.link.classList.toggle('is-active', isActive);
      if (isActive) item.link.setAttribute('aria-current', 'true');
      else item.link.removeAttribute('aria-current');
    });
  };

  let ticking = false;
  const update = () => {
    const shouldShow = window.scrollY > 200;
    const headerIsHidden = headerSection?.classList.contains('shopify-section-header-hidden');
    const navTop = headerIsHidden ? '0px' : 'var(--header-height, 0px)';

    navGroups.forEach((group) => {
      group.section.classList.toggle('is-visible', shouldShow);
      group.section.style.setProperty('--cosmea-product-nav-top', navTop);
      setActiveLink(group);
    });
    ticking = false;
  };
  const requestUpdate = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  };

  update();
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate);

  if (headerSection) {
    const headerObserver = new MutationObserver(requestUpdate);
    headerObserver.observe(headerSection, { attributes: true, attributeFilter: ['class'] });
  }
}

function initCosmeaEditorialMotion() {
  const sections = Array.from(document.querySelectorAll('.cosmea-product-section'));
  if (!sections.length) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const revealSelector = [
    '.cosmea-section-header',
    '.cosmea-product-facts__column',
    '.cosmea-card',
    '.cosmea-benefit-card',
    '.cosmea-ingredient-card',
    '.cosmea-step',
    '.cosmea-timeline-card',
    '.cosmea-trust-badge',
    '.cosmea-visual-story__content',
    '.cosmea-comparison',
    '.cosmea-faq',
    '.cosmea-richtext',
  ].join(',');

  document.documentElement.classList.add('cosmea-motion-ready');

  sections.forEach((section, sectionIndex) => {
    section.dataset.cosmeaReveal = 'section';
    section.style.setProperty('--cosmea-section-index', sectionIndex);

    Array.from(section.querySelectorAll(revealSelector)).forEach((item, itemIndex) => {
      item.dataset.cosmeaReveal = 'item';
      item.style.setProperty('--cosmea-reveal-index', itemIndex % 8);
    });
  });

  if (reduceMotion.matches || !('IntersectionObserver' in window)) {
    sections.forEach((section) => {
      section.classList.add('is-visible', 'is-active');
      section.querySelectorAll('[data-cosmea-reveal="item"]').forEach((item) => item.classList.add('is-visible'));
    });
    return;
  }

  const sectionObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-visible', entry.isIntersecting);
        entry.target.classList.toggle('is-active', entry.intersectionRatio > 0.42);
      });
    },
    {
      rootMargin: '-12% 0px -18% 0px',
      threshold: [0.08, 0.25, 0.42, 0.7],
    }
  );

  const itemObserver = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    },
    {
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.18,
    }
  );

  sections.forEach((section) => {
    sectionObserver.observe(section);
    section.querySelectorAll('[data-cosmea-reveal="item"]').forEach((item) => itemObserver.observe(item));
  });

  let ticking = false;
  const updateProgress = () => {
    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      const distance = window.innerHeight + rect.height;
      const progress = distance > 0 ? (window.innerHeight - rect.top) / distance : 0;
      section.style.setProperty('--cosmea-scroll-progress', Math.max(0, Math.min(1, progress)).toFixed(3));
    });
    ticking = false;
  };

  const requestProgressUpdate = () => {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateProgress);
  };

  updateProgress();
  window.addEventListener('scroll', requestProgressUpdate, { passive: true });
  window.addEventListener('resize', requestProgressUpdate);
}

document.addEventListener('DOMContentLoaded', () => {
  initCosmeaVisualStories();
  initCosmeaStickyBuy();
  initCosmeaProductNav();
  initCosmeaEditorialMotion();
});
